import { Component } from 'react';
import PropTypes from 'prop-types';
import ReactTimeout from 'react-timeout';
import _ from 'lodash';
import {
  callSegmentIdentifyEvent,
  SegmentProfileIdCookies,
  segmentReadyCallback,
  setSegmentContextMVPD,
  setSegmentEntitlements,
} from '@foxcorp/delta-web-analytics';
import { setCookie } from 'helpers/Storage';
import { getMVPDFromCookie, hasEntitlementOverride, analyticsWatcher } from 'helpers/ContentHelper';
import { getAnonymousIdCookie } from 'helpers/AnonymousHelper';

class Segment extends Component {
  constructor(props) {
    super(props);
    this.setAnonymousProfileIdCookie();
    this.state = {
      isAnalyticsReady: false,
    };
  }

  componentDidMount() {
    this.identifyUser();
    const { setTimeout } = this.props;
    const analyticsReadyCheckDelay = 5000;
    setTimeout(() => {
      const { isAnalyticsReady } = this.state;
      if (!isAnalyticsReady) {
        const { analytics } = window;
        if (analytics) analytics.track('Segment Not Ready Error');
      }
    }, analyticsReadyCheckDelay);

    // todo: refactor identifyUser and other dependencies on segment's
    // analytics.ready into this delta-web-analytics callback
    window.analytics?.ready(segmentReadyCallback);
  }

  componentDidUpdate(prevProps) {
    const {
      account,
      loggedInAccountEmail,
      loggedInAnonymousProfileId,
      mvpd,
      entitlements,
    } = this.props;
    const {
      loggedInAccountEmail: prevLoggedInAccountEmail,
      loggedInAnonymousProfileId: prevLoggedInAnonymousProfileId,
      mvpd: prevMvpd,
      entitlements: prevEntitlements,
    } = prevProps;
    const anonymousLoggedInChanged = loggedInAnonymousProfileId !== prevLoggedInAnonymousProfileId;
    const anonymousLoggedInUpdated = loggedInAnonymousProfileId && anonymousLoggedInChanged;
    const emailChanged = prevLoggedInAccountEmail !== loggedInAccountEmail;
    const isEmailUpdated = loggedInAccountEmail && emailChanged;
    const isPreviewPassMvpd = _.includes(mvpd, 'preview pass');
    const previewPassMVPDRemoved = _.includes(prevMvpd, 'preview pass') && mvpd === '';
    const isPreviewPass = isPreviewPassMvpd || previewPassMVPDRemoved;
    const isMvpdChanged = !isPreviewPass && prevMvpd !== mvpd;
    let entitlementsUpdated = !isPreviewPass && entitlements !== prevEntitlements;
    if (mvpd === '' && entitlements !== '') entitlementsUpdated = false;
    const shouldIdentifyUser =
      isEmailUpdated || anonymousLoggedInUpdated || isMvpdChanged || entitlementsUpdated;
    if (account) {
      const userId = _.get(account, 'profileId');
      setCookie({ name: SegmentProfileIdCookies.LAST_KNOWN, value: userId });
    }
    this.setAnonymousProfileIdCookie();
    if (shouldIdentifyUser) this.identifyUser();
  }

  setAnonymousProfileIdCookie = () => {
    const { anonymousAccount } = this.props;
    if (anonymousAccount) {
      const userId = _.get(anonymousAccount, 'profileId');
      setCookie({ name: SegmentProfileIdCookies.LAST_KNOWN_ANONYMOUS, value: userId });
    }
  };

  async identifyUser() {
    await analyticsWatcher();
    const analyticsLoaded = _.get(window, 'analytics');
    const analyticsUser = _.get(window, 'analytics.user');
    if (analyticsLoaded && analyticsUser) {
      const {
        anonymousId,
        entitlements,
        firstIdentifyCall,
        firstSegmentIdentifyCalled,
        loggedInAccountProfileId,
      } = this.props;
      this.setState({ isAnalyticsReady: true });
      const mvpd = getMVPDFromCookie();
      // Preemptively set anonId to prevent segment default from being used
      if (analyticsLoaded.user && !getAnonymousIdCookie())
        analyticsLoaded.user().anonymousId(anonymousId);
      const userId = loggedInAccountProfileId || null;
      const validMvpd = mvpd && !_.includes(mvpd, 'TempPass');
      const entitlementOverride = hasEntitlementOverride();
      let currentEntitlements = entitlements;
      if (entitlementOverride) currentEntitlements = entitlementOverride;
      setSegmentContextMVPD(mvpd);
      setSegmentEntitlements(currentEntitlements);
      const newEntitlements = () => {
        // Allow empty mvpd with entitlements from DCG Profile w/ FX+BT enabled
        if (entitlementOverride) return entitlementOverride;
        return validMvpd ? entitlements : '';
      };
      const segCompProps = {
        profileType: {
          anonymous: !loggedInAccountProfileId,
        },
        mvpd: validMvpd ? mvpd : '',
        entitlements: newEntitlements(),
      };
      callSegmentIdentifyEvent({ eventName: userId, segCompProps });
      if (!firstIdentifyCall) firstSegmentIdentifyCalled();
    }
  }

  render() {
    return null;
  }
}

Segment.defaultProps = {
  account: {},
  anonymousAccount: {},
  anonymousId: null,
  entitlements: null,
  firstSegmentIdentifyCalled: () => {},
  loggedInAccountEmail: null,
  loggedInAccountProfileId: null,
  loggedInAnonymousProfileId: null,
  mvpd: '',
  setTimeout: () => {},
};

Segment.propTypes = {
  account: PropTypes.shape({}),
  anonymousAccount: PropTypes.shape({}),
  anonymousId: PropTypes.string,
  entitlements: PropTypes.oneOfType([PropTypes.shape([]), PropTypes.string]),
  firstIdentifyCall: PropTypes.bool.isRequired,
  firstSegmentIdentifyCalled: PropTypes.func,
  loggedInAccountEmail: PropTypes.string,
  loggedInAccountProfileId: PropTypes.string,
  loggedInAnonymousProfileId: PropTypes.string,
  mvpd: PropTypes.string,
  setTimeout: PropTypes.func,
};

export default ReactTimeout(Segment);
